


import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Tab,
  Tabs,
  Typography,
  Card,
  CardMedia,
  CardContent,
  IconButton,
  Stack,
  Skeleton,
} from "@mui/material";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import axios from "axios";

const PostTab = ({ instagramURL }) => {
  const [tabValue, setTabValue] = useState(0);
  const [influencerData, setInfluencerData] = useState(null);
  const [postImages, setPostImages] = useState([]);
  const [reelData, setReelData] = useState([]);
  const [videoUrls, setVideoUrls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // STEP 1: Fetch initial influencer data
  useEffect(() => {
    if (!instagramURL) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await axios.post(
          "https://server-side-influencer.onrender.com/influencer/add-link",
          { instaUrl: instagramURL }
        );
        setInfluencerData(response.data.data);
        setError("");
      } catch (err) {
        console.error("Error fetching influencer data:", err);
        setError("Error loading influencer data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [instagramURL]);

  // STEP 2: Extract image posts and fetch actual image URLs
  const imagePosts = useMemo(() => {
    return influencerData?.lastPosts?.filter((post) => post.image !== null) || [];
  }, [influencerData]);

  const nullImagePosts = useMemo(() => {
    return influencerData?.lastPosts?.filter((post) => post.image === null) || [];
  }, [influencerData]);

  useEffect(() => {
    if (tabValue === 0 && imagePosts.length > 0 && postImages.length === 0) {
      const fetchPostImages = async () => {
        setLoading(true);
        try {
          const requestData = {
            postUrls: imagePosts.map((post) => post.url),
          };
          const response = await axios.post(
            "https://server-side-influencer.onrender.com/fetch-post-images",
            requestData
          );
          setPostImages(response.data.postImages || []);
        } catch (err) {
          console.error("Error fetching post images:", err);
        } finally {
          setLoading(false);
        }
      };

      fetchPostImages();
    }
  }, [tabValue, imagePosts, postImages.length]);

  // STEP 3: Fetch Reels (video posts)
  useEffect(() => {
    if (tabValue === 1 && reelData.length === 0 && nullImagePosts.length > 0) {
      const fetchReelsData = async () => {
        setLoading(true);
        try {
          const requestData = {
            urls: nullImagePosts.map((post) => post.url),
          };
          const response = await axios.post(
            "https://server-side-influencer.onrender.com/getInstagramData",
            requestData
          );
          setReelData(response.data.results || []);
          setVideoUrls(response.data.results.map((r) => r.video_url));
          setError("");
        } catch (err) {
          console.error("Error fetching reels data:", err);
          setError("An error occurred while fetching reels.");
        } finally {
          setLoading(false);
        }
      };

      fetchReelsData();
    }
  }, [tabValue, nullImagePosts, reelData.length]);

  const formatNumber = (num) => {
    if (num >= 1e6) return (num / 1e6).toFixed(1) + "M";
    if (num >= 1e3) return (num / 1e3).toFixed(1) + "K";
    return num;
  };

  const renderSkeletonCard = (count = 4) =>
    Array.from({ length: count }).map((_, idx) => (
      <Card key={idx} sx={{ width: 320, margin: 2 }}>
        <Skeleton variant="rectangular" height={200} />
        <CardContent>
          <Skeleton variant="text" width="80%" />
          <Skeleton variant="text" width="60%" />
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
            <Skeleton variant="circular" width={24} height={24} />
            <Skeleton variant="text" width={30} />
            <Skeleton variant="circular" width={24} height={24} sx={{ ml: 1 }} />
            <Skeleton variant="text" width={30} />
          </Stack>
        </CardContent>
      </Card>
    ));

  const renderPostCard = (post, index) => {
    const image = postImages[index]?.imageUrl || post.image || "https://via.placeholder.com/150";

    return (
      <Card key={index} sx={{ width: 320, margin: 2 }}>
        <CardMedia component="img" height="200" image={image} alt="Instagram Post" />
        <CardContent>
          <Typography variant="body1" fontWeight="bold" gutterBottom>
            {post.text?.slice(0, 80) || "No Caption"}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <IconButton color="primary">
              <ThumbUpAltIcon />
            </IconButton>
            <Typography variant="body2">{formatNumber(post.likes)}</Typography>

            <IconButton color="secondary">
              <ChatBubbleOutlineIcon />
            </IconButton>
            <Typography variant="body2">{formatNumber(post.comments)}</Typography>
          </Stack>
          {post.url && (
            <a href={post.url} target="_blank" rel="noopener noreferrer">
              View on Instagram
            </a>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderReelCard = (reel, index) => (
    <Card key={index} sx={{ width: 320, margin: 2 }}>
      <CardMedia component="video" height="200" src={reel.video_url} controls />
      <CardContent>
        <Typography variant="body1" fontWeight="bold" gutterBottom>
          {reel.caption?.slice(0, 80) || "Reel"}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <IconButton color="primary">
            <ThumbUpAltIcon />
          </IconButton>
          <Typography variant="body2">{formatNumber(reel.likes)}</Typography>
          <IconButton color="secondary">
            <ChatBubbleOutlineIcon />
          </IconButton>
          <Typography variant="body2">{formatNumber(reel.comments)}</Typography>
        </Stack>
        {reel.url && (
          <a href={reel.url} target="_blank" rel="noopener noreferrer">
            View on Instagram
          </a>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ mt: 4 }}>
      <Tabs value={tabValue} onChange={handleTabChange} aria-label="Posts and Reels Tabs" sx={{ mb: 3 }}>
        <Tab label="Posts" />
        <Tab label="Reels" />
      </Tabs>

      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

      <Box display="flex" flexWrap="wrap" justifyContent="flex-start" pl={2}>
        {loading
          ? renderSkeletonCard()
          : tabValue === 0
          ? imagePosts.map(renderPostCard)
          : reelData.map(renderReelCard)}
      </Box>
    </Box>
  );
};

export default PostTab;
